import { useT } from "../../i18n/hooks.ts";
import { enumerateWindows, type DateRange } from "../../model/aemet/aemet.ts";

/**
 * Progress while an AEMET download is running — shared by AemetFetchDialog.tsx
 * (one sub-basin) and AemetBulkFetchDialog.tsx (one row per sub-basin).
 * Deliberately shows neither a raw "done/total" count nor the word
 * "peticiones" — internally `total` counts date-window chunks (see
 * `STATION_WINDOW_DAYS`, aemet.ts), a number with no meaning to a user who
 * only thinks in terms of sub-basins/dates, and it doesn't scale with
 * anything the user recognizes (a single sub-basin needs exactly as many
 * windows as ten would, for the same date range). Real, meaningful
 * information instead: the actual calendar date already covered, out of the
 * requested range — reusing the same fill-bar look as AemetJobsPopover.tsx's
 * own per-job bar (`.aemet-job-progress*`, styles.css) for visual
 * consistency between the two places progress shows up.
 */
export function AemetDownloadProgress({
  done,
  total,
  range,
}: {
  done: number;
  total: number;
  range: DateRange;
}) {
  const t = useT();
  const windows = enumerateWindows(range);
  const coveredUntil = windows[done - 1]?.end ?? range.start;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="aemet-download-progress" role="status">
      <div className="aemet-job-progress">
        <div className="aemet-job-progress-track">
          <div className="aemet-job-progress-fill" style={{ width: `${percent}%` }} />
        </div>
      </div>
      <p className="muted-text">
        {t("aemetDialog.downloadProgress", {
          from: range.start,
          to: range.end,
          current: coveredUntil,
        })}
      </p>
    </div>
  );
}
